// Leaflet maps.
//
// Both maps take their data from the element they render into, so the
// templates only emit markup and this module owns all the behaviour:
//
//   <div id="map" data-event-ids="1,2,3">   events search, clustered markers
//   <div id="map-area" data-center="lat, lng">  static area map
//
// Import order matters: leaflet-global.js must come first so `window.L` exists
// before the two plugins evaluate — they attach themselves to that global
// rather than importing Leaflet. See leaflet-global.js.
import L from './leaflet-global.js';
import 'beautifymarker';
import 'leaflet.markercluster';
import { onReady } from './dom.js';

const MAPBOX_TOKEN = 'pk.eyJ1Ijoid2hhdHNvbmhlYmRlbiIsImEiOiJjbHI2aWhmdGQwamVoMmpwNGp0b3RtazV0In0.8wS-hwayHNsJf7nhWmdoMQ';

function mapboxTiles(styleId) {
    return L.tileLayer(
        `https://api.mapbox.com/styles/v1/whatsonhebden/${styleId}/tiles/256/{z}/{x}/{y}@2x?access_token=${MAPBOX_TOKEN}`,
        {
            tileSize: 512,
            maxZoom: 19,
            zoomOffset: -1,
            detectRetina: true,
        }
    );
}

// Leaflet can only measure a container it can see. The events map starts in a
// hidden tab, so it needs a nudge once shown / once layout has settled.
function invalidateSoon(map) {
    setTimeout(function () {
        map.invalidateSize();
    }, 100);
}

function setURLSearchParam(key, value) {
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    window.history.pushState({ path: url.href }, '', url.href);
}

function popupHtml(feature) {
    const p = feature.properties;
    return `
        <a class="relative block rounded-lg w-[200px] lg:w-[300px]" href="${p.slug}">
        <div class="relative border-b-2 border-pink">
            <div class="overflow-hidden">
                <img src="${p.image}" class="object-cover rounded-t-lg h-20 lg:h-44 w-full transition-transform group-hover:scale-110 duration-500" />
            </div>
            <span class="absolute -bottom-4 left-1.5 lg:left-4 px-2 py-1 rounded text-white lg:text-sm font-medium bg-pink">${p.category_title}</span>
        </div>
        <div class="pt-5 lg:pt-7 p-3 lg:p-5">
            <h2 class="text-aqua text-base lg:text-xl font-semibold">${p.title}</h2>
            <p class="pt-1 text-sm md:text-base text-pink font-semibold">${p.date}</p>
            <p class="pt-1 text-sm md:text-base text-mine-shaft">${feature.geometry.title}</p>
            <p class="pt-1 text-sm md:text-base text-mine-shaft">${p.is_free ? 'Free' : 'Fee applies'}</p>
        </div>
        </a>`;
}

///////////////////////////////////////////////////////////////////////
// Events search map
///////////////////////////////////////////////////////////////////////
function initEventsMap(el) {
    const map = L.map(el, {
        center: L.latLng(53.74427969396697, -2.0185643647915),
        zoom: 15,
        scrollWheelZoom: false,
    });

    mapboxTiles('clr6iq4zt01eg01r5ddmffhs6').addTo(map);

    // Load markers from the geojson endpoint for the events on this page.
    const markers = L.markerClusterGroup();

    fetch('/events/data.geojson?ids=' + encodeURIComponent(el.dataset.eventIds || ''), {
        headers: { 'Content-Type': 'application/json' },
    })
        .then(function (response) {
            if (response.status !== 200) return null;
            return response.json();
        })
        .then(function (geojson) {
            if (!geojson) return;

            L.geoJSON(geojson, {
                onEachFeature: function (feature, layer) {
                    layer.bindPopup(popupHtml(feature), {
                        closeOnEscapeKey: true,
                        keepInView: true,
                        autoClose: true,
                    });
                },
                style: function (feature) {
                    return { color: feature.properties.category_color };
                },
                pointToLayer: function (feature, latlng) {
                    return L.marker(latlng, {
                        // The marker is a <div role="button" tabindex="0"> with nothing
                        // inside it but an icon font, so a screen reader announced six
                        // anonymous buttons and no way to tell them apart (WCAG 4.1.2
                        // Name, Role, Value). Leaflet's `title` puts the event name on the
                        // element, which gives it an accessible name and a hover tooltip.
                        title: feature.properties.title || 'Event location',
                        icon: L.BeautifyIcon.icon({
                            iconSize: [40, 40],
                            iconStyle: 'box-shadow: 10px 10px 5px -1px rgba(0,0,0,0.50);',
                            innerIconStyle: 'font-size: 18px; line-height: 38px; margin: 0;',
                            icon: feature.properties.category_icon,
                            backgroundColor: '#D3145A',
                            borderWidth: 0,
                            textColor: 'white',
                        }),
                        riseOnHover: true,
                    });
                },
            }).addTo(markers);
        })
        .catch(function () { /* no markers is better than a broken page */ });

    map.addLayer(markers);

    // Map attribution
    map.attributionControl.setPrefix('');
    map.attributionControl.addAttribution('<a href="https://leafletjs.com/" target="_blank">Leaflet</a>, <a href="https://www.mapbox.com/about/maps/" target="_blank">&copy; Mapbox</a>, <a href="http://www.openstreetmap.org/about/" target="_blank">&copy; OpenStreetMap</a> | <a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a>');
    L.control.attribution({ prefix: false, position: 'bottomleft' })
        .addAttribution("<div class='mapbox-wordmark'></div>")
        .addTo(map);

    // List / map tabs. Flowbite does the switching; we keep the URL in step and
    // let Leaflet re-measure once the map tab is actually visible.
    const listTab = document.getElementById('view-list-tab');
    const mapTab = document.getElementById('view-map-tab');

    if (listTab) {
        listTab.addEventListener('click', function () {
            setURLSearchParam('view', 'list');
        });
    }
    if (mapTab) {
        mapTab.addEventListener('click', function () {
            setURLSearchParam('view', 'map');
            invalidateSoon(map);
        });
    }

    // Due to the map being in a tab system, trigger a refresh once everything
    // has loaded so it calculates its size properly.
    window.addEventListener('load', function () {
        invalidateSoon(map);
    });
}

///////////////////////////////////////////////////////////////////////
// Area map (static, centred on the area)
///////////////////////////////////////////////////////////////////////
function parseLatLng(value) {
    const parts = String(value || '').split(',').map(function (s) { return parseFloat(s); });
    if (parts.length !== 2 || parts.some(function (n) { return !Number.isFinite(n); })) {
        return null;
    }
    return L.latLng(parts[0], parts[1]);
}

function initAreaMap(el) {
    // mapCenterPosition is a free-text CMS field ("lat, lng"); don't blow up
    // the page if it's blank or mistyped — just don't draw the map.
    const center = parseLatLng(el.dataset.center);
    if (!center) return;

    const map = L.map(el, {
        center: center,
        zoom: 15,
        zoomControl: false,
        dragging: false,
        scrollWheelZoom: false,
        attributionControl: false,
    });

    mapboxTiles('clxn84rln00im01r2eao89nyi').addTo(map);
}

onReady(function () {
    const eventsMap = document.getElementById('map');
    if (eventsMap && 'eventIds' in eventsMap.dataset) {
        initEventsMap(eventsMap);
    }

    const areaMap = document.getElementById('map-area');
    if (areaMap) {
        initAreaMap(areaMap);
    }
});
